import React from 'react';
import styled from 'styled-components';
import {
  standardColours,
  fontSize,
  minBreakpointQuery,
  brandColours,
  sectionMargins,
  boxShadow,
  standardTransition,
  zIndexLayers,
} from '../styles';
import { Container, Link, Svg, Heading, HtmlContent } from './ui';
import mapPin from '../images/map-pin.inline.svg';
import officeMap from '../images/office-map.svg';
import arrow from '../images/arrow.inline.svg';

const StyledMap = styled.section`
  ${sectionMargins()};
`;

const StyledInner = styled.div`
  display: grid;
  justify-items: center;
  gap: 40px;

  ${minBreakpointQuery.smedium`
    gap: 60px;
  `}

  ${minBreakpointQuery.large`
    gap: 80px;
  `}
`;

const StyledHeader = styled.header`
  text-align: center;
  max-width: 640px;
`;

const StyledText = styled(HtmlContent)`
  margin-top: 20px;
`;

const StyledMapWrapper = styled.div`
  position: relative;
`;

const StyledMapImage = styled.img`
  width: 100%;
  height: auto;
`;

//Moved up for transition
const StyledTooltip = styled.span`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 5px;
  background-color: ${brandColours.secondary};
  color: ${standardColours.white};
  border-radius: 5px;
  opacity: 0;
  pointer-events: none;
  transition: ${standardTransition('opacity')};

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid ${brandColours.secondary};
  }
`;

const StyledPin = styled(Link)`
  position: absolute;
  left: ${({ latitudePercentage }) => `${latitudePercentage}%`};
  top: ${({ longitudePercentage }) => `${longitudePercentage}%`};
  transform: translate(-50%, -50%);

  &:hover {
    z-index: ${zIndexLayers.second};

    ${minBreakpointQuery.smedium`
      ${StyledTooltip} {
        opacity: 1;
        pointer-events: auto;
      }
    `}
  }

  svg {
    height: 10px;
    width: 8px;
    fill: ${brandColours.secondary};

    ${minBreakpointQuery.small`
      height: 15px;
      width: 12px;
    `}

    ${minBreakpointQuery.medium`
      height: 20px;
      width: 16px;
    `}

    ${minBreakpointQuery.large`
      height: 24px;
      width: 19px;
    `}
  }
`;

const StyledList = styled.ul`
  max-width: 780px;
  background-color: ${standardColours.white};
  border-radius: 30px;
  ${boxShadow};
  padding: 20px 40px;

  ${minBreakpointQuery.tsmall`
    columns: 2;
    column-gap: 60px;
    padding: 30px 60px;
  `}

  ${minBreakpointQuery.medium`
    column-gap: 120px;
    padding: 40px 80px;
  `}

  ${minBreakpointQuery.large`
    column-gap: 100px;
    padding: 60px 80px;
  `}
`;

const StyledListItem = styled.li`
  margin-bottom: 24px;
  ${fontSize(18)};

  ${minBreakpointQuery.smedium`
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(24)};
  `}

  &:last-child {
    margin-bottom: 0px;
  }
`;

// moved up for hover
const StyledArrow = styled(Svg)`
  margin-left: 15px;
  opacity: 0;
  transition: ${standardTransition('opacity')};
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;

  &:hover {
    ${StyledArrow} {
      opacity: 1;
    }
  }
`;

const StyledFlag = styled.img`
  height: 24px;
  width: 24px;
  margin-right: 15px;
  object-fit: cover;
  border-radius: 50%;
`;

const MapLocations = ({ heading, text, items }) => (
  <StyledMap>
    <Container>
      <StyledInner>
        <StyledHeader>
          <Heading>{heading}</Heading>
          <StyledText content={text} />
        </StyledHeader>
        <StyledMapWrapper>
          <StyledMapImage src={officeMap} alt="Map of the World" />
          {items.map(
            ({ name, link, latitudePercentage, longitudePercentage }, id) => (
              <StyledPin
                key={id}
                to={link.pageUrl}
                latitudePercentage={latitudePercentage}
                longitudePercentage={longitudePercentage}
              >
                <Svg image={mapPin} />
                <StyledTooltip>{name}</StyledTooltip>
              </StyledPin>
            ),
          )}
        </StyledMapWrapper>
        <StyledList>
          {items.map(({ name, link, flag }, id) => (
            <StyledListItem key={id}>
              <StyledLink to={link.pageUrl}>
                <StyledFlag src={flag.url} alt={flag.alt} loading="lazy" />
                {name}
                <StyledArrow image={arrow} />
              </StyledLink>
            </StyledListItem>
          ))}
        </StyledList>
      </StyledInner>
    </Container>
  </StyledMap>
);
export default MapLocations;
